import React from 'react'
import { Link } from 'gatsby'

import {
  Layout,
  Showcase,
  Grid,
  GridItem,
  Header,
  SectionTitle,
  Intro,
} from '../../../../components'

import './style.scss'
import data from './data.json'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      />
      <Layout
        complete={true}
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      >
        <Intro>
          <p>
            Buttons allow the user to move through a task by triggering single
            actions. The container gives the button visual impact and the text
            label communicates a clear purpose.
          </p>
        </Intro>
        <SectionTitle>Variants</SectionTitle>
        <Showcase
          site={data.site}
          text="Used for the primary action in a layout, this button drives the user forward in a task. Only use one primary button per screen."
        >
          <button class="c-button--primary">Primary Button</button>
        </Showcase>
        <Showcase
          site={data.site}
          text="Used for any alternate actions in a layout, this button lets the user know what they can do instead of moving forward in a task. Only use when a primary button is also present. Multiple default buttons can appear in a single screen."
        >
          <button class="c-button">Default Button</button>
        </Showcase>
        <Showcase
          site={data.site}
          container={true}
          text="Used instead of the default button, this button achieves a cleaner layout. Only use when a primary button is also present. Multiple subtle buttons can appear in a single screen."
        >
          <button class="c-button--subtle">Subtle Button</button>
        </Showcase>
        <hr />
        <SectionTitle>States</SectionTitle>
        <p>
          In addition to the default state, every button has a hover/active
          state and a focus state. Optional states for disabled and loading are
          also available.
        </p>
        <Showcase
          site={data.site}
          title="Disabled"
          text="Used for an unavailable action, this state signals that the user must complete another action first. Only use when a triggering action, such as choosing between two options shown on screen, must be completed before the button’s action becomes available."
        >
          <button class="c-button c-button--disabled" aria-disabled="true">
            Disabled Button
          </button>
        </Showcase>
        <Showcase
          site={data.site}
          title="Loading"
          text="Used when a button initiates a longer action, this state signals to the user that their request will be completed shortly. Only use when the expected wait time is between 5 and 15 seconds. Loading buttons animate with a trailing ellipsis until the action completes."
        >
          <button class="c-button--primary-loading l-margin-bottom--small">
            <span class="c-button--primary-loading__item-1" />
            <span class="c-button--primary-loading__item-2" />
            <span class="c-button--primary-loading__item-3" />
          </button>
          <button class="c-button-loading l-margin-bottom--small">
            <span class="c-button-loading__item-1" />
            <span class="c-button-loading__item-2" />
            <span class="c-button-loading__item-3" />
          </button>
          <div class="showcase__component-container">
            <button class="c-button--subtle-loading">
              <span class="c-button--subtle-loading__item-1" />
              <span class="c-button--subtle-loading__item-2" />
              <span class="c-button--subtle-loading__item-3" />
            </button>
          </div>
        </Showcase>
        <hr />
        <SectionTitle>Design Environment</SectionTitle>
        <p>
          Primary buttons will always take prominent placement to the right of
          or on top of other buttons. Inline buttons should follow the{' '}
          <Link
            class="link"
            to={'/' + data.site + '/patterns/action-group/design'}
          >
            dual-inline action group
          </Link>{' '}
          pattern. No more than two individual buttons should appear
          side-by-side in a single layout. In mobile environments, single
          buttons will automatically fill the full width of the screen. In
          tablet and desktop environments, buttons will fill 100% of their
          containing element.
        </p>
        <Showcase
          site={data.site}
          text="Keep single buttons at a distance of 32px from the previous component. When stacking buttons, any buttons placed below a primary button should leave a margin of 8px in between. "
        >
          <button class="c-button--primary">Primary Button</button>
          <button class="c-button l-margin-top--xsmall">
            Secondary Button
          </button>
        </Showcase>
        <Showcase
          site={data.site}
          text="Only use inline placement when there is a clear relationship between the primary and secondary actions being offered. Primary buttons always appear to the right of any other inline button. Buttons should be of equal width and separated in the center by a margin of at least 16px."
          note="The spacing between buttons will look different in the DSM design library. Since we are designing on a 375px artboard, the space between buttons will likely be an uneven value. This is okay as long as the margin between the buttons in the final product is consistent."
        >
          <div class="u-flex-container">
            <button class="c-button l-margin-right--medium">Inline 1</button>
            <button class="c-button--primary">Inline 2</button>
          </div>
        </Showcase>
        <hr />
        <div class="microcopy">
          <div class="microcopy__text">
            <SectionTitle>Microcopy</SectionTitle>
            <p>
              Use strong verbs and specific language in text labels to clearly
              signal what will happen next.
            </p>
            <p>
              Text should be in title case with no end punctuation. Ideal length
              is between 5 and 25 characters.
            </p>
          </div>
          <Grid>
            <GridItem columns="6">
              <div class="do">
                <div class="do__title">
                  <i class="icon icon-Success"></i> Do
                </div>
                <div>
                  <button class="c-button--subtle">Get Started</button>
                  <p>
                    <strong>Use</strong> a clear call to action.
                  </p>
                </div>
                <div>
                  <button class="c-button--subtle">Complete Deposit</button>
                  <p>
                    <strong>Use</strong> title case.
                  </p>
                </div>
                <div>
                  <button class="c-button--subtle">Make a Payment</button>
                  <p>
                    Keep text as <strong>short</strong> while still being clear.
                  </p>
                </div>
              </div>
            </GridItem>
            <GridItem columns="6">
              <div class="dont">
                <div class="dont__title">
                  <i class="icon icon-Error"></i> Don't
                </div>
                <div>
                  <button class="c-button--subtle">Click Here</button>
                  <p>
                    <strong>Use</strong> generic language.
                  </p>
                </div>
                <div>
                  <button class="c-button--subtle">Complete deposit.</button>
                  <p>
                    <strong>Use</strong> sentence case or end punctation.
                  </p>
                </div>
                <div>
                  <button class="c-button--subtle">
                    Make a Payment from...
                  </button>
                  <p>
                    Try to include <strong>too much</strong> information.
                  </p>
                </div>
              </div>
            </GridItem>
          </Grid>
        </div>
      </Layout>
    </>
  )
}
